import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../config";
import { useTranslation } from "react-i18next";

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const [serverError, setServerError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validating, setValidating] = useState(true);

  // Simulate token validation (optional: can be used for real validation on mount)
  useEffect(() => {
    if (!token || !email) {
      setServerError(t("Invalid or missing reset link."));
      setValidating(false);
    } else {
      setValidating(false); // Replace with backend token check if needed
    }
  }, [token, email, t]);

  const onSubmit = async (data) => {
    setServerError(null);

    if (data.password !== data.confirmPassword) {
      return toast.error(t("Passwords do not match"), {
        position: "top-center",
        transition: Bounce,
      });
    }

    try {
      const response = await fetch(`${config.apiUrl}/clients/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          email,
          newPassword: data.password,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(t("Password reset successful! You can now login."), {
          position: "top-center",
          autoClose: 5000,
          transition: Bounce,
        });
        setSuccess(true);
        reset();
      } else {
        setServerError(result?.error || t("Reset failed. Try again."));
      }
    } catch (error) {
      console.error("Reset error:", error);
      setServerError(t("Something went wrong. Try again."));
    }
  };

  return (
    <div className="min-h-screen bg-[#fdf6ec] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md bg-white shadow-lg rounded-3xl px-6 py-8">
        <h2 className="text-xl font-bold text-center text-[#0a2942] mb-4">
          {t("Reset Password")}
        </h2>

        {validating ? (
          <p className="text-center text-sm text-gray-500">{t("Validating link...")}</p>
        ) : serverError ? (
          <div className="text-center text-red-600">
            <p>{serverError}</p>
          </div>
        ) : success ? (
          <div className="text-center text-green-700 space-y-2">
            <p>{t("✅ Your password has been reset successfully.")}</p>
            <a href="/login" className="text-blue-600 hover:underline">
              {t("Go to Login")}
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <input
                type="password"
                {...register("password", { required: t("Password is required"), minLength: 6 })}
                placeholder={t("New password")}
                className="w-full px-4 py-3 rounded-full bg-[#f0f9ff] focus:ring-2 focus:ring-[#47b5ff]"
              />
              {errors.password && (
                <p className="text-xs text-red-500">{errors.password.message}</p>
              )}
            </div>

            <div>
              <input
                type="password"
                {...register("confirmPassword", { required: t("Confirm password") })}
                placeholder={t("Confirm password")}
                className="w-full px-4 py-3 rounded-full bg-[#f0f9ff] focus:ring-2 focus:ring-[#47b5ff]"
              />
              {errors.confirmPassword && (
                <p className="text-xs text-red-500">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-slate-800 hover:bg-[#ff9933] text-white font-bold py-3 rounded-full transition duration-200"
            >
              {t("Reset Password")}
            </button>

            {serverError && (
              <p className="text-xs text-red-500 text-center">{serverError}</p>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
